/**
 * Offline Storage Service using IndexedDB
 * Provides local storage for supervision forms, patient data, employee data, and pending actions
 */

export interface OfflinePatient {
  id?: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  email?: string;
  phone?: string;
  address?: string;
  addressCity?: string;
  addressState?: string;
  addressZip?: string;
  insuranceType?: string;
  lastSupervisionDate?: string | null;
  nextSupervisionDue?: string | null;
  status: 'active' | 'discharged';
  created: string;
  updated: string;
  syncStatus: 'synced' | 'pending' | 'failed';
}

export interface OfflineEmployee {
  id?: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  role: string;
  department?: string;
  status: 'active' | 'inactive';
  created: string;
  updated: string;
  syncStatus: 'synced' | 'pending' | 'failed';
}

export interface OfflineSupervision {
  id?: string;
  employeeId: string;
  patientId: string;
  supervisorId: string;
  workflowId?: string; // Added to track associated workflow for cleanup
  formData: Record<string, any>;
  signatures: {
    employee?: string;
    supervisor?: string;
    patient?: string;
  };
  status: 'draft' | 'completed' | 'locked';
  created: string;
  updated: string;
  syncStatus: 'synced' | 'pending' | 'failed';
}

export interface OfflinePendingAction {
  id?: string;
  type: 'patient' | 'employee' | 'supervision' | 'user';
  action: 'create' | 'update' | 'delete';
  data: any;
  timestamp: number;
  retryCount: number;
  lastError?: string;
}

class OfflineStorageService {
  private dbName = 'LighthouseOfflineDB';
  private dbVersion = 1;
  private db: IDBDatabase | null = null;

  private stores = {
    patients: 'patients',
    employees: 'employees',
    supervisions: 'supervisions',
    pendingActions: 'pendingActions',
    metadata: 'metadata'
  };

  /**
   * Initialize the IndexedDB database
   */
  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => {
        console.error('Failed to open IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        console.log('IndexedDB initialized successfully');
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        
        // Create patients store
        if (!db.objectStoreNames.contains(this.stores.patients)) {
          const patientsStore = db.createObjectStore(this.stores.patients, { 
            keyPath: 'id', 
            autoIncrement: true 
          });
          patientsStore.createIndex('syncStatus', 'syncStatus', { unique: false });
          patientsStore.createIndex('status', 'status', { unique: false });
          patientsStore.createIndex('updated', 'updated', { unique: false });
        }

        // Create employees store
        if (!db.objectStoreNames.contains(this.stores.employees)) {
          const employeesStore = db.createObjectStore(this.stores.employees, { 
            keyPath: 'id', 
            autoIncrement: true 
          });
          employeesStore.createIndex('syncStatus', 'syncStatus', { unique: false });
          employeesStore.createIndex('status', 'status', { unique: false });
          employeesStore.createIndex('email', 'email', { unique: true });
        }

        // Create supervisions store
        if (!db.objectStoreNames.contains(this.stores.supervisions)) {
          const supervisionsStore = db.createObjectStore(this.stores.supervisions, { 
            keyPath: 'id', 
            autoIncrement: true 
          });
          supervisionsStore.createIndex('syncStatus', 'syncStatus', { unique: false });
          supervisionsStore.createIndex('status', 'status', { unique: false });
          supervisionsStore.createIndex('employeeId', 'employeeId', { unique: false });
          supervisionsStore.createIndex('patientId', 'patientId', { unique: false });
          supervisionsStore.createIndex('created', 'created', { unique: false });
        }

        // Create pending actions store
        if (!db.objectStoreNames.contains(this.stores.pendingActions)) {
          const actionsStore = db.createObjectStore(this.stores.pendingActions, { 
            keyPath: 'id', 
            autoIncrement: true 
          });
          actionsStore.createIndex('type', 'type', { unique: false });
          actionsStore.createIndex('timestamp', 'timestamp', { unique: false });
          actionsStore.createIndex('retryCount', 'retryCount', { unique: false });
        }

        // Create metadata store for sync timestamps and other info
        if (!db.objectStoreNames.contains(this.stores.metadata)) {
          db.createObjectStore(this.stores.metadata, { keyPath: 'key' });
        }

        console.log('IndexedDB stores created/updated');
      };
    });
  }

  /**
   * Get a transaction for the specified stores
   */
  private getTransaction(storeNames: string | string[], mode: IDBTransactionMode = 'readonly'): IDBTransaction {
    if (!this.db) {
      throw new Error('Database not initialized. Call init() first.');
    }
    return this.db.transaction(storeNames, mode);
  }

  /**
   * Get an object store
   */
  private getStore(storeName: string, mode: IDBTransactionMode = 'readonly'): IDBObjectStore {
    const transaction = this.getTransaction(storeName, mode);
    return transaction.objectStore(storeName);
  }

  // PATIENT OPERATIONS

  async savePatient(patient: OfflinePatient): Promise<string> {
    const store = this.getStore(this.stores.patients, 'readwrite');
    const patientData: OfflinePatient = {
      ...patient,
      updated: new Date().toISOString(),
      syncStatus: patient.syncStatus ?? 'pending',
    };

    return new Promise((resolve, reject) => {
      const request = store.put(patientData);
      request.onsuccess = () => resolve(request.result as string);
      request.onerror = () => reject(request.error);
    });
  }

  async getPatients(status?: 'active' | 'discharged'): Promise<OfflinePatient[]> {
    const store = this.getStore(this.stores.patients);
    
    return new Promise((resolve, reject) => {
      let request: IDBRequest;
      
      if (status) {
        const index = store.index('status');
        request = index.getAll(status);
      } else {
        request = store.getAll();
      }
      
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getPatient(id: string): Promise<OfflinePatient | null> {
    const store = this.getStore(this.stores.patients);
    
    return new Promise((resolve, reject) => {
      const request = store.get(id);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  async deletePatient(id: string): Promise<void> {
    const store = this.getStore(this.stores.patients, 'readwrite');

    return new Promise((resolve, reject) => {
      const request = store.delete(id);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Replace the synced subset of the patient cache with the server's view.
   * - Server patients are upserted with syncStatus: 'synced'.
   * - Existing 'synced' records that are NOT in the server list are deleted
   *   (e.g. patient was discharged out of scope, deleted, or scope changed).
   * - Records with syncStatus 'pending' or 'failed' are preserved — they carry
   *   local edits that haven't reached the server yet.
   */
  async replacePatients(serverPatients: OfflinePatient[]): Promise<void> {
    const existing = await this.getPatients();
    const serverIds = new Set(
      serverPatients.map(p => p.id).filter((id): id is string => !!id)
    );
    const toDelete = existing.filter(
      p => p.id && p.syncStatus === 'synced' && !serverIds.has(p.id)
    );

    if (!this.db) {
      throw new Error('Database not initialized. Call init() first.');
    }
    const tx = this.db.transaction(this.stores.patients, 'readwrite');
    const store = tx.objectStore(this.stores.patients);
    const now = new Date().toISOString();

    return new Promise((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);

      for (const stale of toDelete) {
        if (stale.id) store.delete(stale.id);
      }
      for (const p of serverPatients) {
        store.put({ ...p, updated: now, syncStatus: 'synced' as const });
      }
    });
  }

  // EMPLOYEE OPERATIONS

  async saveEmployee(employee: OfflineEmployee): Promise<string> {
    const store = this.getStore(this.stores.employees, 'readwrite');
    const employeeData: OfflineEmployee = {
      ...employee,
      updated: new Date().toISOString(),
      syncStatus: employee.syncStatus ?? 'pending',
    };

    return new Promise((resolve, reject) => {
      const request = store.put(employeeData);
      request.onsuccess = () => resolve(request.result as string);
      request.onerror = () => reject(request.error);
    });
  }

  async getEmployees(status?: 'active' | 'inactive'): Promise<OfflineEmployee[]> {
    const store = this.getStore(this.stores.employees);
    
    return new Promise((resolve, reject) => {
      let request: IDBRequest;
      
      if (status) {
        const index = store.index('status');
        request = index.getAll(status);
      } else {
        request = store.getAll();
      }
      
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getEmployee(id: string): Promise<OfflineEmployee | null> {
    const store = this.getStore(this.stores.employees);

    return new Promise((resolve, reject) => {
      const request = store.get(id);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Replace the synced subset of the employee cache with the server's view.
   * See replacePatients for the prune-vs-preserve semantics.
   */
  async replaceEmployees(serverEmployees: OfflineEmployee[]): Promise<void> {
    const existing = await this.getEmployees();
    const serverIds = new Set(
      serverEmployees.map(e => e.id).filter((id): id is string => !!id)
    );
    const toDelete = existing.filter(
      e => e.id && e.syncStatus === 'synced' && !serverIds.has(e.id)
    );

    if (!this.db) {
      throw new Error('Database not initialized. Call init() first.');
    }
    const tx = this.db.transaction(this.stores.employees, 'readwrite');
    const store = tx.objectStore(this.stores.employees);
    const now = new Date().toISOString();

    return new Promise((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);

      for (const stale of toDelete) {
        if (stale.id) store.delete(stale.id);
      }
      for (const e of serverEmployees) {
        store.put({ ...e, updated: now, syncStatus: 'synced' as const });
      }
    });
  }

  // SUPERVISION OPERATIONS

  async saveSupervision(supervision: OfflineSupervision): Promise<string> {
    const store = this.getStore(this.stores.supervisions, 'readwrite');
    const supervisionData = {
      ...supervision,
      updated: new Date().toISOString(),
      syncStatus: 'pending' as const
    };
    
    return new Promise((resolve, reject) => {
      const request = store.put(supervisionData);
      request.onsuccess = () => resolve(request.result as string);
      request.onerror = () => reject(request.error);
    });
  }

  async getSupervisions(filters?: {
    employeeId?: string;
    patientId?: string;
    status?: 'draft' | 'completed' | 'locked';
  }): Promise<OfflineSupervision[]> {
    const store = this.getStore(this.stores.supervisions);
    
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      
      request.onsuccess = () => {
        let results = request.result;
        
        // Apply filters
        if (filters) {
          if (filters.employeeId) {
            results = results.filter(s => s.employeeId === filters.employeeId);
          }
          if (filters.patientId) {
            results = results.filter(s => s.patientId === filters.patientId);
          }
          if (filters.status) {
            results = results.filter(s => s.status === filters.status);
          }
        }
        
        resolve(results);
      };
      request.onerror = () => reject(request.error);
    });
  }

  async getSupervision(id: string): Promise<OfflineSupervision | null> {
    const store = this.getStore(this.stores.supervisions);
    
    return new Promise((resolve, reject) => {
      const request = store.get(id);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  // PENDING ACTIONS

  async savePendingAction(action: OfflinePendingAction): Promise<string> {
    const store = this.getStore(this.stores.pendingActions, 'readwrite');
    
    return new Promise((resolve, reject) => {
      const request = store.put(action);
      request.onsuccess = () => resolve(request.result as string);
      request.onerror = () => reject(request.error);
    });
  }

  async getPendingActions(): Promise<OfflinePendingAction[]> {
    const store = this.getStore(this.stores.pendingActions);
    
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async deletePendingAction(id: string): Promise<void> {
    const store = this.getStore(this.stores.pendingActions, 'readwrite');
    
    return new Promise((resolve, reject) => {
      const request = store.delete(id);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // METADATA AND SYNC

  async setMetadata(key: string, value: any): Promise<void> {
    const store = this.getStore(this.stores.metadata, 'readwrite');
    
    return new Promise((resolve, reject) => {
      const request = store.put({ key, value });
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getMetadata(key: string): Promise<any> {
    const store = this.getStore(this.stores.metadata);
    
    return new Promise((resolve, reject) => {
      const request = store.get(key);
      request.onsuccess = () => resolve(request.result?.value);
      request.onerror = () => reject(request.error);
    });
  }

  async getLastSyncTime(): Promise<Date | null> {
    const timestamp = await this.getMetadata('lastSyncTime');
    return timestamp ? new Date(timestamp) : null;
  }

  async setLastSyncTime(date: Date = new Date()): Promise<void> {
    await this.setMetadata('lastSyncTime', date.toISOString());
  }

  async getUnsyncedData(): Promise<{
    patients: OfflinePatient[];
    employees: OfflineEmployee[];
    supervisions: OfflineSupervision[];
  }> {
    const [patients, employees, supervisions] = await Promise.all([
      this.getPatients(),
      this.getEmployees(),
      this.getSupervisions()
    ]);

    return {
      patients: patients.filter(p => p.syncStatus !== 'synced'),
      employees: employees.filter(e => e.syncStatus !== 'synced'),
      supervisions: supervisions.filter(s => s.syncStatus !== 'synced')
    };
  }

  async markAsSynced(type: 'patient' | 'employee' | 'supervision', id: string): Promise<void> {
    let storeName: string;
    
    switch (type) {
      case 'patient':
        storeName = this.stores.patients;
        break;
      case 'employee':
        storeName = this.stores.employees;
        break;
      case 'supervision':
        storeName = this.stores.supervisions;
        break;
      default:
        throw new Error(`Unknown type: ${type}`);
    }

    const store = this.getStore(storeName, 'readwrite');
    
    return new Promise((resolve, reject) => {
      const getRequest = store.get(id);
      
      getRequest.onsuccess = () => {
        const item = getRequest.result;
        if (item) {
          item.syncStatus = 'synced';
          const putRequest = store.put(item);
          putRequest.onsuccess = () => resolve();
          putRequest.onerror = () => reject(putRequest.error);
        } else {
          resolve(); // Item doesn't exist, consider it handled
        }
      };
      
      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  /**
   * Clear all offline data (for testing or reset purposes)
   */
  async clearAllData(): Promise<void> {
    const storeNames = Object.values(this.stores);
    const transaction = this.getTransaction(storeNames, 'readwrite');
    
    const promises = storeNames.map(storeName => {
      return new Promise<void>((resolve, reject) => {
        const store = transaction.objectStore(storeName);
        const request = store.clear();
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    });

    await Promise.all(promises);
  }

  /**
   * Close the database connection
   */
  close(): void {
    if (this.db) {
      this.db.close();
      this.db = null;
    }
  }
}

// Export singleton instance
export const offlineStorage = new OfflineStorageService();
export default offlineStorage;